// This code has been fully generated by an AI.

import { inject, Service } from '@angular/core';
import { filter, switchMap, take, tap } from 'rxjs';
import { EmployeeBasicDto, EmployeeCoreDto } from '@app/_api/dto';
import { UUID } from '@app/_api/identifyable';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '@app/shared/confirmation-dialog/confirmation-dialog.component';
import { DialogService } from '@app/core/services/dialog.service';
import { EmployeeDataService } from '@app/core/services/employee-data.service';
import { TreeViewDataService } from '@app/core/services/tree-view.data.service';

export interface EmployeeViewSwitchTarget {
  uuid: UUID;
  firstname: string;
  lastname: string;
}

@Service()
export class EmployeeViewSwitchService {
  private readonly dialogService = inject(DialogService);
  private readonly employeeDataService = inject(EmployeeDataService);
  private readonly treeViewDataService = inject(TreeViewDataService);

  switchToEmployee(target: EmployeeViewSwitchTarget) {
    const loggedInEmployee = this.employeeDataService.employee();
    const currentView = this.treeViewDataService.$employeeView();

    // may be undefined if the user is not logged in (or if the employee data has not been loaded yet)
    if (!loggedInEmployee) return;

    // Already viewing the target employee, no need to switch.
    if (currentView?.uuid === target.uuid) return;

    // selecting oneself clears the foreign view, no need to open the confirmation dialog.
    if (target.uuid === loggedInEmployee.uuid) {
      this.treeViewDataService.$employeeView.set(undefined);
      this.dialogService.closeAll();
      return;
    }

    const dialogRef = this.dialogService.open(ConfirmationDialogComponent, {
      data: {
        title: 'Ansicht wechseln?',
        message: `Möchtest Du auf ${target.firstname} ${target.lastname}s Profil wechseln?`,
        confirmText: 'Wechseln',
        cancelText: 'Abbrechen',
      } satisfies ConfirmationDialogModel,
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter(confirmed => confirmed === true),
        switchMap(() => this.employeeDataService.getEmployeeById(target.uuid)),
        tap(employeeToSwitchTo => {
          this.treeViewDataService.$employeeView.set(this.toEmployeeView(employeeToSwitchTo));
          this.dialogService.closeAll();
        }),
      )
      .subscribe();
  }

  private toEmployeeView(employee: EmployeeBasicDto): EmployeeCoreDto {
    return {
      uuid: employee.uuid,
      firstname: employee.firstname,
      lastname: employee.lastname,
      lastConfirmed: employee.lastConfirmed,
      hasChangedSinceLastConfirmed: employee.hasChangedSinceLastConfirmed,
    };
  }
}
